import { useEffect, useRef, useState } from 'react';
import { Sun, Moon } from 'lucide-react';
import { useTheme } from '../theme';

const CAPS = [
  'Hola, le llamo de parte de la empresa. ¿Tiene un minuto?',
  'Le recuerdo su cita agendada para el jueves a las 10 a.m.',
  'Tenemos una promoción que puede interesarle, le cuento…',
  'Perfecto, le agendo y le confirmo por mensaje. ¡Gracias!',
];

const WAVE_BARS = Array.from({ length: 30 }, (_, i) => ({
  dur: 0.7 + ((i * 37) % 60) / 100,   // 0.7–1.3s
  delay: ((i * 53) % 90) / 100,       // 0–0.9s
}));

// Shell de autenticación: hero de marca a la izquierda (desktop) + formulario a la derecha.
export default function AuthLayout({ children }) {
  const canvasRef = useRef(null);
  const { theme, toggle } = useTheme();
  const [sec, setSec] = useState(47);
  const [cap, setCap] = useState(0);

  useEffect(() => {
    const t1 = setInterval(() => setSec(s => s + 1), 1000);
    const t2 = setInterval(() => setCap(c => (c + 1) % CAPS.length), 3400);
    return () => { clearInterval(t1); clearInterval(t2); };
  }, []);
  const mmss = `${String(Math.floor(sec / 60)).padStart(2, '0')}:${String(sec % 60).padStart(2, '0')}`;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let animId, t = 0;
    const resize = () => { canvas.width = canvas.offsetWidth; canvas.height = canvas.offsetHeight; };
    resize();
    window.addEventListener('resize', resize);
    const waves = [
      { amp: 46, freq: 0.012, speed: 0.018, phase: 0,   color: 'rgba(124,58,237,0.22)' },
      { amp: 30, freq: 0.020, speed: 0.025, phase: 2.0, color: 'rgba(109,40,217,0.16)'  },
      { amp: 60, freq: 0.008, speed: 0.012, phase: 4.0, color: 'rgba(45,212,191,0.12)' },
      { amp: 22, freq: 0.030, speed: 0.030, phase: 1.0, color: 'rgba(167,139,250,0.14)' },
    ];
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const cy = canvas.height * 0.7;
      waves.forEach(w => {
        ctx.beginPath();
        for (let x = 0; x <= canvas.width; x += 3) {
          const y = cy + Math.sin(x * w.freq + t * w.speed + w.phase) * w.amp
                       + Math.sin(x * w.freq * 2.1 + t * w.speed * 0.6 + w.phase) * (w.amp * 0.35);
          x === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
        }
        ctx.strokeStyle = w.color; ctx.lineWidth = 1.6; ctx.stroke();
      });
      t++; animId = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(animId); window.removeEventListener('resize', resize); };
  }, []);

  return (
    <div style={{ position: 'relative', minHeight: '100vh', background: 'var(--bg)', display: 'flex', overflow: 'hidden' }}>

      {/* Fondo full-page: ondas + glows (detrás de todo) */}
      <canvas ref={canvasRef} style={{ position: 'fixed', inset: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 0, opacity: 0.9 }} />
      <div style={{ position: 'fixed', top: '36%', left: '20%', width: 540, height: 540, borderRadius: '50%', background: 'radial-gradient(circle, var(--accent-soft) 0%, transparent 70%)', pointerEvents: 'none', zIndex: 0 }} />
      <div style={{ position: 'fixed', bottom: '2%', right: '10%', width: 440, height: 440, borderRadius: '50%', background: 'radial-gradient(circle, rgba(45,212,191,0.10) 0%, transparent 70%)', pointerEvents: 'none', zIndex: 0 }} />

      {/* Theme toggle */}
      <button onClick={toggle} aria-label={theme === 'dark' ? 'Modo claro' : 'Modo oscuro'} style={{
        position: 'absolute', top: 20, right: 24, zIndex: 6,
        width: 38, height: 38, borderRadius: 11, cursor: 'pointer',
        background: 'var(--glass)', border: '1px solid var(--glass-brd)', color: 'var(--text-2)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        WebkitBackdropFilter: 'blur(16px)', backdropFilter: 'blur(16px)',
        boxShadow: 'inset 0 1px 0 var(--glass-hi)',
      }}>
        {theme === 'dark' ? <Sun size={17} /> : <Moon size={17} />}
      </button>

      {/* ── HERO (desktop) ── */}
      <div className={theme === 'dark' ? 'ic-auth-hero ic-auth-hero--recorrido' : 'ic-auth-hero'}>
        {theme === 'dark' ? (
          /* Recorrido animado por los 12 modulos (vectorial, en vivo). En claro se ve el hero de siempre. */
          <iframe src="/animacion/recorrido.htm?v=1" title="InnovaCall: recorrido por sus módulos" aria-hidden="true" tabIndex={-1}
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 0, background: 'transparent', pointerEvents: 'none', zIndex: 1 }} />
        ) : (<>

        {/* Logo */}
        <div className="ic-reveal" style={{ position: 'relative', zIndex: 2, display: 'flex', alignItems: 'center', gap: 12, animationDelay: '0.05s' }}>
          <div style={{ width: 40, height: 40, borderRadius: 11, overflow: 'hidden', background: 'linear-gradient(135deg,#7c3aed,#6d28d9)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 6px 22px rgba(124,58,237,0.5)' }}>
            <img src="/logo.png" alt="InnovaCall" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { e.target.style.display = 'none'; }} />
          </div>
          <div style={{ fontFamily: 'Inter,sans-serif', fontWeight: 800, fontSize: 20, color: 'var(--text)' }}>
            Innova<span style={{ color: 'var(--accent-solid)' }}>Call</span>
          </div>
        </div>

        {/* Mensaje */}
        <div style={{ position: 'relative', zIndex: 2 }}>
          <h1 className="ic-reveal" style={{ fontFamily: 'Inter,sans-serif', fontSize: 29, fontWeight: 800, lineHeight: 1.14, color: 'var(--text)', letterSpacing: '-0.02em', marginBottom: 12, textWrap: 'balance', animationDelay: '0.12s' }}>
            Campañas con voz IA,<br /><span style={{ color: 'var(--accent)' }}>en piloto automático.</span>
          </h1>
          <p className="ic-reveal" style={{ fontSize: 14.5, color: 'var(--text-2)', lineHeight: 1.55, maxWidth: 430, marginBottom: 18, animationDelay: '0.2s' }}>
            Voces humanas que llaman, conversan y resuelven por ti — ventas, citas, encuestas, cobros y más, a cualquier escala.
          </p>

          {/* Mini-demo: llamada en vivo */}
          <div className="ic-reveal" style={{
            marginBottom: 14, maxWidth: 400,
            background: 'var(--glass)', border: '1px solid var(--glass-brd)', borderRadius: 16,
            WebkitBackdropFilter: 'blur(20px) saturate(1.5)', backdropFilter: 'blur(20px) saturate(1.5)',
            boxShadow: 'var(--shadow-sm), inset 0 1px 0 var(--glass-hi)', padding: '15px 18px',
            animationDelay: '0.3s',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
              <div style={{ width: 38, height: 38, borderRadius: '50%', background: 'linear-gradient(135deg,#7c3aed,#6d28d9)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 15, flexShrink: 0, boxShadow: '0 4px 14px rgba(124,58,237,0.45)' }}>A</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text)' }}>Andrea</div>
                <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>Voz IA · en llamada</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 600, color: 'var(--teal)' }}>
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--teal)', boxShadow: '0 0 8px var(--teal)', animation: 'pulse 1.6s ease-in-out infinite' }} />
                En vivo
                <span style={{ color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums', marginLeft: 4 }}>{mmss}</span>
              </div>
            </div>

            {/* Onda de audio */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 3, height: 30, margin: '14px 0 12px' }}>
              {WAVE_BARS.map((b, i) => (
                <span key={i} style={{
                  flex: 1, height: '100%', borderRadius: 2,
                  background: 'linear-gradient(180deg, var(--accent), var(--teal))',
                  transformOrigin: 'center',
                  animation: `icWave ${b.dur}s ease-in-out ${b.delay}s infinite`,
                }} />
              ))}
            </div>

            {/* Caption rotativo */}
            <div style={{ fontSize: 12.5, color: 'var(--text-2)', lineHeight: 1.45, minHeight: 36 }}>
              <span key={cap} style={{ display: 'inline-block', animation: 'icFadeIn 0.4s ease' }}>
                <span style={{ color: 'var(--accent)', fontWeight: 600 }}>Andrea:</span> {CAPS[cap]}
              </span>
            </div>
          </div>

        </div>

        {/* Pie hero */}
        <div className="ic-reveal" style={{ position: 'relative', zIndex: 2, fontSize: 12, color: 'var(--text-3)', animationDelay: '0.6s' }}>
          © {new Date().getFullYear()} InnovaCall · by Innovabot
        </div>
        </>)}
      </div>

      {/* ── FORM ── */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 20px', position: 'relative', zIndex: 2 }}>
        {/* Logo compacto (solo móvil) */}
        <div className="ic-auth-mobilelogo ic-reveal" style={{ alignItems: 'center', gap: 12, marginBottom: 28, animationDelay: '0.05s' }}>
          <div style={{ width: 40, height: 40, borderRadius: 11, overflow: 'hidden', background: 'linear-gradient(135deg,#7c3aed,#6d28d9)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 6px 22px rgba(124,58,237,0.5)' }}>
            <img src="/logo.png" alt="InnovaCall" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={e => { e.target.style.display = 'none'; }} />
          </div>
          <div style={{ fontFamily: 'Inter,sans-serif', fontWeight: 800, fontSize: 20, color: 'var(--text)' }}>
            Innova<span style={{ color: 'var(--accent-solid)' }}>Call</span>
          </div>
        </div>
        {children}
      </div>
    </div>
  );
}
